import React, {useEffect, useState} from "react";
import {NavLink} from "react-router-dom";
import {useHistory} from "react-router";
import {observer} from "mobx-react-lite";
import MenuItem from "../../atoms/menu/menuItem";
import {IconSource} from "../../../shared/enums/iconSource";
import {RoutePaths} from "../../../shared/routePaths";
import {QueryParams} from "../../../shared/enums/queryParams";
import AccordionGroup from "../../molecules/accordionGroup/accordionGroup";
import {residentAdminRoute} from "../../../routes";
import {useTranslation} from "react-i18next";
import {useStores} from "../../../stores/useStores";
import {
  allVillageKeyValue,
  multiVillageKeyValue
} from "../../../shared/constants";
import "./navbarResidentsMenu.sass";

export interface NavbarResidentsMenuProps {
  initialIsExpanded?: boolean;
  villageId?: string;
  currentPathName: string;
  permanentlyExpanded?: boolean;
}

const NavbarResidentsMenu: React.FC<NavbarResidentsMenuProps> = ({
  initialIsExpanded = false,
  villageId,
  currentPathName,
  permanentlyExpanded = false
}) => {
  const {t} = useTranslation(["translation"]);
  const history = useHistory();
  const {
    domainStore: {residentActionRequiredCountsDomainStore}
  } = useStores();
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);

  useEffect(() => {
    setIsExpanded(initialIsExpanded);
  }, [initialIsExpanded]);

  const isConcreteVillage =
    !!villageId &&
    villageId !== multiVillageKeyValue.value &&
    villageId !== allVillageKeyValue.value;

  useEffect(() => {
    if (isConcreteVillage && villageId) {
      residentActionRequiredCountsDomainStore.loadCounts(villageId);
    }
  }, [isConcreteVillage, villageId, residentActionRequiredCountsDomainStore]);

  const residentsName = t("translation:titles.residentsGroup");
  const villageIdQueryParam = `?${QueryParams.VillageId}=${villageId}`;

  const isParentSelected =
    currentPathName === RoutePaths.IndependentResidentAdmin ||
    currentPathName === RoutePaths.ServicedApartmentResidentAdmin;

  const subItems = [
    {
      to: RoutePaths.IndependentResidentAdmin,
      title: t("translation:titles.independentResidents"),
      icon: IconSource.featured,
      badgeCount: isConcreteVillage
        ? residentActionRequiredCountsDomainStore.independent
        : undefined
    },
    {
      to: RoutePaths.ServicedApartmentResidentAdmin,
      title: t("translation:titles.servicedApartmentResidents"),
      icon: IconSource.favourite,
      badgeCount: isConcreteVillage
        ? residentActionRequiredCountsDomainStore.servicedApartment
        : undefined
    }
  ];

  const children = subItems.map(({to, title, icon, badgeCount}) => {
    const isSelected = currentPathName === to;
    return (
      <NavLink key={to} className="nav-link" to={to + villageIdQueryParam}>
        <MenuItem
          title={title}
          icon={icon}
          selected={isSelected}
          scoped={isSelected}
          badgeCount={badgeCount}
          badgeLabel={t("translation:labels.residentsRequiringAction", {
            count: badgeCount
          })}
        />
      </NavLink>
    );
  });

  return (
    <div className="navbar-residents-menu">
      <AccordionGroup
        accordions={[
          {
            id: residentsName,
            header: () => (
              <MenuItem
                title={residentsName}
                icon={residentAdminRoute.icon}
                selected={isParentSelected}
                scoped={isParentSelected}
              />
            ),
            children,
            toggleIcon: IconSource.toggleUp,
            toggleIconColor:
              permanentlyExpanded || isExpanded ? "primary" : "secondary",
            isExpanded: permanentlyExpanded || isExpanded,
            hideToggleIcon: permanentlyExpanded,
            onAccordionClick: (_, isExpanded) => {
              if (permanentlyExpanded) {
                history.push(
                  RoutePaths.IndependentResidentAdmin + villageIdQueryParam
                );
              } else {
                setIsExpanded(isExpanded);
              }
            }
          }
        ]}
      />
    </div>
  );
};

export default observer(NavbarResidentsMenu);
